// Color variables
const colors = {
  background: `#f2f2f2`,
  textPrimary: `#1F1F24`,
  commentGray: `#9B9B9B`,
  purple: `#5028C0`,
  lineNumberGray: `hsl(230, 1%, 62%)`,
  toolbarBackground: `hsl(230, 1%, 90%)`,
  toolbarText: `hsl(230, 6%, 44%)`,
  toolbarHoverBackground: `hsl(230, 1%, 78%)`,
  toolbarHoverText: `hsl(230, 8%, 24%)`,
  lineHighlightBackground: `hsla(230, 8%, 24%, 0.05)`,
  lineHighlightBorder: `hsla(230, 8%, 24%, 0.2)`,
  white: `hsl(0, 0%, 100%)`,
  deletedRed: `hsla(353, 100%, 66%, 0.15)`,
  deletedRedSelection: `hsla(353, 95%, 66%, 0.25)`,
  insertedGreen: `hsla(137, 100%, 55%, 0.15)`,
  insertedGreenSelection: `hsla(135, 73%, 55%, 0.25)`,
  bracesRed: `hsl(5, 74%, 59%)`,
  bracesBlue: `hsl(221, 87%, 60%)`,
  bracesPurple: `hsl(301, 63%, 40%)`,
  previewBorder: `hsl(0, 0, 95%)`,
};

// Font family variable
const monospaceFonts = `"Apercu Mono Pro", "Fira Code", "Fira Mono", Menlo, Consolas, "DejaVu Sans Mono", monospace`;

// Common styles for code and pre elements
const commonCodeStyles = {
  background: colors.background,
  color: colors.textPrimary,
  fontFamily: monospaceFonts,
  direction: `ltr`,
  textAlign: `left`,
  whiteSpace: `pre`,
  wordSpacing: `normal`,
  wordBreak: `normal`,
  lineHeight: `1.5`,
  MozTabSize: `2`,
  OTabSize: `2`,
  tabSize: `2`,
  WebkitHyphens: `none`,
  MozHyphens: `none`,
  msHyphens: `none`,
  hyphens: `none`,
};

// Common styles for selections
const commonSelectionStyles = {
  background: colors.toolbarBackground,
  color: `inherit`,
};

export const mixpanelTheme = {
  'code[class*="language-"]': {
    ...commonCodeStyles,
  },
  'pre[class*="language-"]': {
    ...commonCodeStyles,
    padding: `16px`,
    marginTop: `8px`,
    overflow: `auto`,
    borderRadius: `4px`,
  },
  'code[class*="language-"]::-moz-selection': commonSelectionStyles,
  'code[class*="language-"] *::-moz-selection': commonSelectionStyles,
  'pre[class*="language-"] *::-moz-selection': commonSelectionStyles,
  'code[class*="language-"]::selection': commonSelectionStyles,
  'code[class*="language-"] *::selection': commonSelectionStyles,
  'pre[class*="language-"] *::selection': commonSelectionStyles,
  ':not(pre) > code[class*="language-"]': {
    padding: `0.2em 0.3em`,
    borderRadius: `0.3em`,
    whiteSpace: `normal`,
  },
  comment: {
    color: colors.commentGray,
  },
  prolog: {
    color: colors.commentGray,
  },
  cdata: {
    color: colors.commentGray,
  },
  boolean: {
    color: colors.purple,
  },
  selector: {
    color: colors.purple,
  },
  string: {
    color: colors.purple,
  },
  char: {
    color: colors.purple,
  },
  builtin: {
    color: colors.purple,
  },
  inserted: {
    color: colors.purple,
  },
  regex: {
    color: colors.purple,
  },
  'attr-value': {
    color: colors.purple,
  },
  'attr-value > .token.punctuation': {
    color: colors.purple,
  },
  '.language-css .token.url > .token.string.url': {
    color: colors.purple,
  },
  '.language-markdown .token.blockquote.punctuation': {
    color: colors.commentGray,
    fontStyle: `italic`,
  },
  '.language-markdown .token.hr.punctuation': {
    color: colors.commentGray,
    fontStyle: `italic`,
  },
  '.language-markdown .token.code-snippet': {
    color: colors.purple,
  },
  bold: {
    fontWeight: `600`,
  },
  italic: {
    fontStyle: `italic`,
  },
  namespace: {
    Opacity: `0.8`,
  },
  'token.tab:not(:empty):before': {
    color: `hsla(230, 8%, 24%, 0.2)`,
  },
  'token.cr:before': {
    color: colors.lineHighlightBorder,
  },
  'token.lf:before': {
    color: colors.lineHighlightBorder,
  },
  'token.space:before': {
    color: colors.lineHighlightBorder,
  },
  'div.code-toolbar > .toolbar.toolbar > .toolbar-item': {
    marginRight: `0.4em`,
  },
  'div.code-toolbar > .toolbar.toolbar > .toolbar-item > button': {
    background: colors.toolbarBackground,
    color: colors.toolbarText,
    padding: `0.1em 0.4em`,
    borderRadius: `0.3em`,
  },
  'div.code-toolbar > .toolbar.toolbar > .toolbar-item > a': {
    background: colors.toolbarBackground,
    color: colors.toolbarText,
    padding: `0.1em 0.4em`,
    borderRadius: `0.3em`,
  },
  'div.code-toolbar > .toolbar.toolbar > .toolbar-item > span': {
    background: colors.toolbarBackground,
    color: colors.toolbarText,
    padding: `0.1em 0.4em`,
    borderRadius: `0.3em`,
  },
  'div.code-toolbar > .toolbar.toolbar > .toolbar-item > button:hover': {
    background: colors.toolbarHoverBackground,
    color: colors.toolbarHoverText,
  },
  'div.code-toolbar > .toolbar.toolbar > .toolbar-item > button:focus': {
    background: colors.toolbarHoverBackground,
    color: colors.toolbarHoverText,
  },
  'div.code-toolbar > .toolbar.toolbar > .toolbar-item > a:hover': {
    background: colors.toolbarHoverBackground,
    color: colors.toolbarHoverText,
  },
  'div.code-toolbar > .toolbar.toolbar > .toolbar-item > a:focus': {
    background: colors.toolbarHoverBackground,
    color: colors.toolbarHoverText,
  },
  'div.code-toolbar > .toolbar.toolbar > .toolbar-item > span:hover': {
    background: colors.toolbarHoverBackground,
    color: colors.toolbarHoverText,
  },
  'div.code-toolbar > .toolbar.toolbar > .toolbar-item > span:focus': {
    background: colors.toolbarHoverBackground,
    color: colors.toolbarHoverText,
  },
  '.line-highlight.line-highlight': {
    background: colors.lineHighlightBackground,
  },
  '.line-highlight.line-highlight:before': {
    background: colors.toolbarBackground,
    color: colors.toolbarHoverText,
    padding: `0.1em 0.6em`,
    borderRadius: `0.3em`,
    boxShadow: `0 2px 0 0 rgba(0, 0, 0, 0.2)`,
  },
  '.line-highlight.line-highlight[data-end]:after': {
    background: colors.toolbarBackground,
    color: colors.toolbarHoverText,
    padding: `0.1em 0.6em`,
    borderRadius: `0.3em`,
    boxShadow: `0 2px 0 0 rgba(0, 0, 0, 0.2)`,
  },
  'pre[id].linkable-line-numbers.linkable-line-numbers span.line-numbers-rows > span:hover:before': {
    backgroundColor: colors.lineHighlightBackground,
  },
  '.line-numbers.line-numbers .line-numbers-rows': {
    borderRightColor: colors.lineHighlightBorder,
  },
  '.command-line .command-line-prompt': {
    borderRightColor: colors.lineHighlightBorder,
  },
  '.line-numbers .line-numbers-rows > span:before': {
    color: colors.lineNumberGray,
  },
  '.command-line .command-line-prompt > span:before': {
    color: colors.lineNumberGray,
  },
  '.rainbow-braces .token.token.punctuation.brace-level-1': {
    color: colors.bracesRed,
  },
  '.rainbow-braces .token.token.punctuation.brace-level-5': {
    color: colors.bracesRed,
  },
  '.rainbow-braces .token.token.punctuation.brace-level-9': {
    color: colors.bracesRed,
  },
  '.rainbow-braces .token.token.punctuation.brace-level-2': {
    color: colors.purple,
  },
  '.rainbow-braces .token.token.punctuation.brace-level-6': {
    color: colors.purple,
  },
  '.rainbow-braces .token.token.punctuation.brace-level-10': {
    color: colors.purple,
  },
  '.rainbow-braces .token.token.punctuation.brace-level-3': {
    color: colors.bracesBlue,
  },
  '.rainbow-braces .token.token.punctuation.brace-level-7': {
    color: colors.bracesBlue,
  },
  '.rainbow-braces .token.token.punctuation.brace-level-11': {
    color: colors.bracesBlue,
  },
  '.rainbow-braces .token.token.punctuation.brace-level-4': {
    color: colors.bracesPurple,
  },
  '.rainbow-braces .token.token.punctuation.brace-level-8': {
    color: colors.bracesPurple,
  },
  '.rainbow-braces .token.token.punctuation.brace-level-12': {
    color: colors.bracesPurple,
  },
  'pre.diff-highlight > code .token.token.deleted:not(.prefix)': {
    backgroundColor: colors.deletedRed,
  },
  'pre > code.diff-highlight .token.token.deleted:not(.prefix)': {
    backgroundColor: colors.deletedRed,
  },
  'pre.diff-highlight > code .token.token.deleted:not(.prefix)::-moz-selection': {
    backgroundColor: colors.deletedRedSelection,
  },
  'pre.diff-highlight > code .token.token.deleted:not(.prefix) *::-moz-selection': {
    backgroundColor: colors.deletedRedSelection,
  },
  'pre > code.diff-highlight .token.token.deleted:not(.prefix)::-moz-selection': {
    backgroundColor: colors.deletedRedSelection,
  },
  'pre > code.diff-highlight .token.token.deleted:not(.prefix) *::-moz-selection': {
    backgroundColor: colors.deletedRedSelection,
  },
  'pre.diff-highlight > code .token.token.deleted:not(.prefix)::selection': {
    backgroundColor: colors.deletedRedSelection,
  },
  'pre.diff-highlight > code .token.token.deleted:not(.prefix) *::selection': {
    backgroundColor: colors.deletedRedSelection,
  },
  'pre > code.diff-highlight .token.token.deleted:not(.prefix)::selection': {
    backgroundColor: colors.deletedRedSelection,
  },
  'pre > code.diff-highlight .token.token.deleted:not(.prefix) *::selection': {
    backgroundColor: colors.deletedRedSelection,
  },
  'pre.diff-highlight > code .token.token.inserted:not(.prefix)': {
    backgroundColor: colors.insertedGreen,
  },
  'pre > code.diff-highlight .token.token.inserted:not(.prefix)': {
    backgroundColor: colors.insertedGreen,
  },
  'pre.diff-highlight > code .token.token.inserted:not(.prefix)::-moz-selection': {
    backgroundColor: colors.insertedGreenSelection,
  },
  'pre.diff-highlight > code .token.token.inserted:not(.prefix) *::-moz-selection': {
    backgroundColor: colors.insertedGreenSelection,
  },
  'pre > code.diff-highlight .token.token.inserted:not(.prefix)::-moz-selection': {
    backgroundColor: colors.insertedGreenSelection,
  },
  'pre > code.diff-highlight .token.token.inserted:not(.prefix) *::-moz-selection': {
    backgroundColor: colors.insertedGreenSelection,
  },
  'pre.diff-highlight > code .token.token.inserted:not(.prefix)::selection': {
    backgroundColor: colors.insertedGreenSelection,
  },
  'pre.diff-highlight > code .token.token.inserted:not(.prefix) *::selection': {
    backgroundColor: colors.insertedGreenSelection,
  },
  'pre > code.diff-highlight .token.token.inserted:not(.prefix)::selection': {
    backgroundColor: colors.insertedGreenSelection,
  },
  'pre > code.diff-highlight .token.token.inserted:not(.prefix) *::selection': {
    backgroundColor: colors.insertedGreenSelection,
  },
  '.prism-previewer.prism-previewer:before': {
    borderColor: colors.previewBorder,
  },
  '.prism-previewer-gradient.prism-previewer-gradient div': {
    borderColor: colors.previewBorder,
    borderRadius: `0.3em`,
  },
  '.prism-previewer-color.prism-previewer-color:before': {
    borderRadius: `0.3em`,
  },
  '.prism-previewer-easing.prism-previewer-easing:before': {
    borderRadius: `0.3em`,
  },
  '.prism-previewer.prism-previewer:after': {
    borderTopColor: colors.previewBorder,
  },
  '.prism-previewer-flipped.prism-previewer-flipped.after': {
    borderBottomColor: colors.previewBorder,
  },
  '.prism-previewer-angle.prism-previewer-angle:before': {
    background: colors.white,
  },
  '.prism-previewer-time.prism-previewer-time:before': {
    background: colors.white,
  },
  '.prism-previewer-easing.prism-previewer-easing': {
    background: colors.white,
  },
  '.prism-previewer-angle.prism-previewer-angle circle': {
    stroke: colors.toolbarHoverText,
    strokeOpacity: `1`,
  },
  '.prism-previewer-time.prism-previewer-time circle': {
    stroke: colors.toolbarHoverText,
    strokeOpacity: `1`,
  },
  '.prism-previewer-easing.prism-previewer-easing circle': {
    stroke: colors.toolbarHoverText,
    fill: `transparent`,
  },
  '.prism-previewer-easing.prism-previewer-easing path': {
    stroke: colors.toolbarHoverText,
  },
  '.prism-previewer-easing.prism-previewer-easing line': {
    stroke: colors.toolbarHoverText,
  },
};
